import { useEffect, useRef, useState } from "react";

/**
 * Reveal children with a smooth fade-up animation when the element
 * scrolls into view. Uses IntersectionObserver — runs once per element.
 *
 * Props:
 *  - delay: ms delay before showing (e.g. 100, 200)
 *  - y: initial y offset in px (default 24)
 *  - as: element tag, defaults to div
 */
export default function FadeIn({ as: Tag = "div", delay = 0, y = 24, className = "", children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const style = {
    transform: visible ? "translateY(0)" : `translateY(${y}px)`,
    opacity: visible ? 1 : 0,
    transition: `opacity 800ms ease-out ${delay}ms, transform 800ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
    willChange: "opacity, transform",
  };

  return (
    <Tag ref={ref} className={className} style={style} {...rest}>
      {children}
    </Tag>
  );
}
